/**
 * Admin Page
 *
 * Purpose: Container page for admin functionality including document library management,
 * prompt templates, and user management
 *
 * Features:
 * - Document library management (pgvector / Sprint 3.5)
 * - Prompt template management (Sprint 4 / Airline Customization)
 * - User management with approval workflow (Sprint 5)
 * - Super Admin access to system configuration
 *
 * Access: Admin/Instructor role only
 *
 * Sprint: 5 (User Onboarding)
 * Author: Edward Berger + AI Assistant
 * Date: 2025-11-01
 */

import { useState, useEffect } from 'react';
import { DocumentsAdmin } from '../components/admin/DocumentsAdmin';
import { PromptTemplatesAdmin } from '../components/admin/PromptTemplatesAdmin';
import { TranscriptionScenariosAdmin } from '../components/admin/TranscriptionScenariosAdmin';
import { UsersAdmin } from './admin/UsersAdmin';
import PilotsAdmin from './admin/PilotsAdmin';
import { useAuth } from '../contexts/AuthContext';
import { useAirline } from '../contexts/AirlineContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { FileText, MessageSquare, Mic, Shield, UserCircle, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';

type AdminTab = 'documents' | 'prompts' | 'scenarios' | 'users' | 'pilots';

export default function Admin() {
  const { user } = useAuth();
  const { isSuperAdmin } = useAirline();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('documents');
  const [pendingUserCount, setPendingUserCount] = useState(0);

  // Check if user has admin role
  const isAdmin = user?.user_metadata?.role === 'admin' || user?.user_metadata?.role === 'instructor';
  // Narrower: airline admins only (not instructors). Used to gate User Management
  // and Prompt Templates — both are admin-only configuration surfaces.
  const isAirlineAdmin = user?.user_metadata?.role === 'admin';

  // If a non-admin somehow has activeTab pointing at an admin-only tab
  // (URL state, hot-reload, role downgrade, etc.), bounce them to documents.
  useEffect(() => {
    if (activeTab === 'prompts' && !(isAirlineAdmin || isSuperAdmin)) {
      setActiveTab('documents');
    }
    if (activeTab === 'users' && !(isAirlineAdmin || isSuperAdmin)) {
      setActiveTab('documents');
    }
  }, [activeTab, isAirlineAdmin, isSuperAdmin]);

  // Fetch pending user count for badge (super admins only)
  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchPendingCount = async () => {
      try {
        const { count, error } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('approval_status', 'pending');

        if (!error && count !== null) {
          setPendingUserCount(count);
        }
      } catch (err) {
        console.error('Failed to fetch pending user count:', err);
      }
    };

    fetchPendingCount();

    // Set up real-time subscription for pending users
    const subscription = supabase
      .channel('pending-users-count')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: 'approval_status=eq.pending',
        },
        () => {
          fetchPendingCount();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [isSuperAdmin]);

  // Redirect non-admins to dashboard
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orca-elevate">
        <h1 className="text-3xl font-bold text-navy">Admin Panel</h1>
        <p className="text-steel mt-2">
          Manage system settings, document libraries, prompt templates, and user approvals
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex border-b border-steel/20">
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex items-center gap-2 px-6 py-4 font-semibold transition ${
              activeTab === 'documents'
                ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5'
                : 'text-steel hover:bg-ice'
            }`}
          >
            <FileText size={20} />
            <span>Document Library</span>
          </button>
          {(isAirlineAdmin || isSuperAdmin) && (
            <button
              onClick={() => setActiveTab('prompts')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition ${
                activeTab === 'prompts'
                  ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5'
                  : 'text-steel hover:bg-ice'
              }`}
            >
              <MessageSquare size={20} />
              <span>Prompt Templates</span>
            </button>
          )}
          <button
            onClick={() => setActiveTab('scenarios')}
            className={`flex items-center gap-2 px-6 py-4 font-semibold transition ${
              activeTab === 'scenarios'
                ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5'
                : 'text-steel hover:bg-ice'
            }`}
          >
            <Mic size={20} />
            <span>Transcription Scenarios</span>
          </button>
          <button
            onClick={() => setActiveTab('pilots')}
            className={`flex items-center gap-2 px-6 py-4 font-semibold transition ${
              activeTab === 'pilots'
                ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5'
                : 'text-steel hover:bg-ice'
            }`}
          >
            <UserCircle size={20} />
            <span>Pilots</span>
          </button>
          {(isAirlineAdmin || isSuperAdmin) && (
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition ${
                activeTab === 'users'
                  ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5'
                  : 'text-steel hover:bg-ice'
              }`}
            >
              <Users size={20} />
              <span>User Management</span>
              {isSuperAdmin && pendingUserCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingUserCount}
                </span>
              )}
            </button>
          )}
          {isSuperAdmin && (
            <button
              onClick={() => navigate('/admin/doc-types')}
              className="flex items-center gap-2 px-6 py-4 font-semibold text-steel hover:bg-ice transition"
            >
              <Shield size={20} />
              <span>Super Admin</span>
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'documents' && <DocumentsAdmin />}
        {activeTab === 'prompts' && (isAirlineAdmin || isSuperAdmin) && <PromptTemplatesAdmin />}
        {activeTab === 'scenarios' && <TranscriptionScenariosAdmin />}
        {activeTab === 'pilots' && <PilotsAdmin />}
        {activeTab === 'users' && (isAirlineAdmin || isSuperAdmin) && <UsersAdmin />}
      </div>
    </div>
  );
}
